import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, BIKE_STATUS } from "../config";
import SiempriaCredit from "../components/SiempriaCredit";
import { Activity, ArrowDown, ArrowLeft, ArrowUp, Bike, CalendarClock, HardDrive, Maximize2, Minimize2, RefreshCw, ShieldCheck, Trophy, Volume2, VolumeX } from "lucide-react";

// Ajusta el tamaño del texto para que el nombre entre en la casilla sin desbordar ni cortarse.
function FitText({ children, max = 14, min = 8, maxLines = 2, className = "", testid }) {
  const ref = useRef(null);
  const [size, setSize] = useState(max);
  const [tick, setTick] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let s = max, guard = 0;
    el.style.fontSize = s + "px";
    // reduce hasta que el texto quepa en maxLines líneas sin desbordar la casilla
    while (el.scrollHeight > s * 1.15 * maxLines + 0.5 && s > min && guard < 80) {
      s -= 0.5; el.style.fontSize = s + "px"; guard++;
    }
    setSize(s);
  }, [children, max, min, maxLines, tick]);
  useEffect(() => {
    const onResize = () => setTick((t) => t + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return (
    <p ref={ref} data-testid={testid} className={className}
       style={{ fontSize: size + "px", lineHeight: 1.15, overflow: "hidden",
                display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: maxLines }}>
      {children}
    </p>
  );
}

const REFRESH_MS = 30000;
const REVIEW_ALERT_DAYS = 7; // motos "OK" sin revisar en X días parpadean en la pantalla
const daysSinceReview = (b) => {
  const raw = b.status_updated_at || b.created_at;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
};
const bikeImg = (b) => (b.image_path ? `${API}/bikes/image/${b.slug}` : b.image);

// Mini-calendario de ocupación de la semana para la Pantalla (solo lectura)
const WD_OFFICE = ["L", "M", "X", "J", "V", "S", "D"];
const CH_CELL = { online: "bg-topacio-green", partner: "bg-blue-500", tienda: "bg-topacio-red" };
function WeekOccupancy({ cal, overdueRefs }) {
  if (!cal) return null;
  const days = cal.day_list || [];
  const closed = new Set(cal.closed_dates || []);
  const todayISO = new Date().toISOString().slice(0, 10);
  const bySlug = {};
  (cal.bookings || []).forEach((bk) => { (bySlug[bk.bike_slug] = bySlug[bk.bike_slug] || []).push(bk); });
  const rows = (cal.bikes || []).filter((b) => (bySlug[b.slug] || []).length);
  const covers = (bk, iso) => bk.start_date <= iso && iso < bk.end_date;
  const wd = (iso) => { const [y, m, d] = iso.split("-").map(Number); return (new Date(y, m - 1, d).getDay() + 6) % 7; };
  const dayNum = (iso) => Number(iso.split("-")[2]);
  return (
    <section className="rounded-3xl border-2 border-white/10 bg-white/[0.035] p-5 mt-6" data-testid="office-week-calendar">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-black text-xl flex items-center gap-2"><CalendarClock className="w-5 h-5 text-brand" /> Ocupación de la semana</h2>
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.15em] font-extrabold">
          <span className="flex items-center gap-1 text-topacio-green"><ArrowUp className="w-3.5 h-3.5" /> Recoge hoy</span>
          <span className="flex items-center gap-1 text-topacio-red"><ArrowDown className="w-3.5 h-3.5" /> Devuelve hoy</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500 font-semibold">Sin reservas esta semana.</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-max">
            <div className="flex">
              <div style={{ width: 150 }} className="shrink-0" />
              {days.map((iso) => (
                <div key={iso} style={{ width: 46 }} className={`shrink-0 text-center ${iso === todayISO ? "text-brand" : "text-zinc-400"}`}>
                  <div className={`text-[9px] font-extrabold ${wd(iso) >= 5 ? "text-topacio-red" : ""}`}>{WD_OFFICE[wd(iso)]}</div>
                  <div className="text-xs font-black">{dayNum(iso)}</div>
                </div>
              ))}
            </div>
            {rows.map((bike) => (
              <div key={bike.slug} data-testid={`office-week-row-${bike.slug}`} className="flex items-center border-t border-white/5 py-1">
                <div style={{ width: 150 }} className="shrink-0 pr-2">
                  <FitText max={12} min={8} maxLines={1} className="font-bold text-white">{bike.name}</FitText>
                </div>
                {days.map((iso) => {
                  const list = bySlug[bike.slug] || [];
                  const bk = list.find((x) => covers(x, iso));
                  const isClosed = closed.has(iso);
                  const isToday = iso === todayISO;
                  const pickup = list.find((x) => x.start_date === iso);
                  const ret = list.find((x) => x.end_date === iso);
                  const todayPickup = isToday && pickup;
                  const todayReturn = isToday && ret;
                  const isLate = todayReturn && ret && overdueRefs?.has(ret.ref);
                  const ringCls = isLate ? "ring-2 ring-topacio-red animate-pulse"
                    : todayPickup ? "ring-2 ring-topacio-green"
                    : todayReturn ? "ring-2 ring-topacio-red" : "";
                  const baseCls = bk ? (CH_CELL[bk.channel] || "bg-topacio-green")
                    : isLate ? "bg-topacio-red"
                    : todayReturn ? "bg-topacio-red/20"
                    : isClosed ? "bg-white/[0.04]" : "bg-white/[0.015] border border-white/5";
                  return (
                    <div key={iso} style={{ width: 46 }} className="shrink-0 px-0.5">
                      <div data-testid={isLate ? `office-overdue-cell-${bike.slug}` : todayPickup ? `office-pickup-today-${bike.slug}` : todayReturn ? `office-return-today-${bike.slug}` : undefined}
                           title={bk ? `${bk.name || ""} · ${bk.channel || "web"}` : ret ? `${isLate ? "RETRASADA · " : "Devuelve: "}${ret.name || ""}` : ""}
                           className={`h-6 rounded flex items-center justify-center ${baseCls} ${ringCls}`}>
                        {todayPickup && <ArrowUp className="w-3.5 h-3.5 text-white drop-shadow" />}
                        {!todayPickup && todayReturn && <ArrowDown className={`w-3.5 h-3.5 ${isLate ? "text-white" : "text-topacio-red"}`} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
const statusMeta = (status) => BIKE_STATUS[status || "ok"] || BIKE_STATUS.ok;
// etiquetas cortas para la pantalla de oficina (se leen de un vistazo a distancia)
const SHORT_LABELS = { alquilada: "En la calle", pendiente_pieza: "Pend. pieza", pendiente_limpiar: "Pend. limpiar", retornada: "Retornada", presupuesto: "Presupuesto", revisada: "Revisada", danada: "Dañada", en_lavadero: "En lavadero", sin_itv: "Sin ITV", sin_seguro: "Sin seguro" };
const statusLabel = (status) => SHORT_LABELS[status || "ok"] || statusMeta(status).label;
const statusRank = (status) => ({ danada: 0, sin_itv: 1, sin_seguro: 1, alquilada: 2, por_reparar: 3, pendiente_pieza: 4, presupuesto: 5, bloqueada: 6, en_lavadero: 7, por_revisar: 8, pendiente_limpiar: 9, retornada: 10, revisada: 11 }[status || "ok"] ?? 12);
const eur = (n) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n || 0);
// "3h", "2d", "45 min" a partir de minutos de retraso
const lateLabel = (min) => {
  const m = Math.max(0, Math.round(min || 0));
  if (m >= 1440) return `${Math.floor(m / 1440)}d`;
  if (m >= 60) return `${Math.floor(m / 60)}h`;
  return `${m} min`;
};

function KioskKpi({ label, value, sub, icon: Icon, tone = "text-brand" }) {
  return (
    <div className="rounded-2xl border-2 border-white/10 bg-white/[0.04] p-5">
      <div className="flex items-center justify-between text-[10px] font-extrabold uppercase tracking-[0.2em] text-zinc-500">
        {label} <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className="font-display font-black text-4xl text-white mt-3 leading-none">{value}</div>
      <div className="text-xs font-semibold text-zinc-500 mt-2">{sub}</div>
    </div>
  );
}

export default function OfficeDisplayPage() {
  const navigate = useNavigate();
  const [noc, setNoc] = useState(null);
  const [bikes, setBikes] = useState([]);
  const [cal, setCal] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthHistory, setHealthHistory] = useState(null);
  const [now, setNow] = useState(new Date());
  const [fullscreen, setFullscreen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("office_sound_on") === "1");
  const audioCtxRef = useRef(null);
  const prevOverdueRef = useRef(null); // null = aún sin inicializar
  const token = localStorage.getItem("admin_token");

  const load = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [n, b, h, hh, c] = await Promise.all([
        axios.get(`${API}/admin/noc`, { headers }),
        axios.get(`${API}/admin/bikes`, { headers }),
        axios.get(`${API}/admin/server-health`, { headers }),
        axios.get(`${API}/admin/server-health/history?hours=24`, { headers }),
        axios.get(`${API}/admin/calendar?days=7`, { headers }),
      ]);
      setNoc(n.data);
      setBikes(b.data.bikes);
      setHealth(h.data);
      setHealthHistory(hh.data);
      setCal(c.data);
    } catch {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    const dataTimer = setInterval(load, REFRESH_MS);
    const clockTimer = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(dataTimer); clearInterval(clockTimer); };
  }, [token, navigate, load]);

  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { /* el navegador puede bloquearlo si no hay gesto */ }
  };

  // --- Aviso sonoro de retraso (Web Audio: beeps sin fichero) ---
  const ensureAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
  }, []);

  const playAlert = useCallback((beeps = 3) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const t0 = ctx.currentTime;
    for (let i = 0; i < beeps; i++) {
      const t = t0 + i * 0.35;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = i === beeps - 1 ? 1100 : 880;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.3);
    }
  }, []);

  const sortedBikes = useMemo(() => [...bikes].sort((a, b) => statusRank(a.status) - statusRank(b.status) || a.name.localeCompare(b.name)), [bikes]);
  const compactFleet = bikes.length > 24;
  const reviewThreshold = noc?.review_alert_days || REVIEW_ALERT_DAYS;
  const staleCount = useMemo(() => bikes.filter((b) => (b.status || "ok") === "ok" && daysSinceReview(b) >= reviewThreshold).length, [bikes, reviewThreshold]);
  const onStreet = Math.max(noc?.fleet?.rented_today || 0, bikes.filter((b) => b.status === "alquilada").length);
  const overdueReturns = useMemo(() => noc?.overdue_returns || [], [noc]);
  const overdueRefs = useMemo(() => new Set(overdueReturns.map((o) => o.ref)), [overdueReturns]);
  const onTimeReturns = useMemo(() => (noc?.returns_today || []).filter((r) => !r.overdue), [noc]);
  const healthTone = health?.status === "ok" ? "bg-topacio-green text-white" : health?.status === "warning" ? "bg-brand text-topacio-ink" : "bg-topacio-red text-white";

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("office_sound_on", next ? "1" : "0");
    if (next) {
      ensureAudio();
      // a partir de ahora solo suenan los NUEVOS retrasos; confirmamos con un beep
      prevOverdueRef.current = new Set(overdueReturns.map((o) => o.ref));
      playAlert(1);
    }
  };

  // suena cuando aparece un retraso nuevo (y el sonido está activado)
  useEffect(() => {
    const curr = new Set(overdueReturns.map((o) => o.ref));
    const prev = prevOverdueRef.current;
    if (soundOn && prev !== null) {
      const hasNew = [...curr].some((r) => !prev.has(r));
      if (hasNew) { ensureAudio(); playAlert(3); }
    }
    if (prev !== null || soundOn) prevOverdueRef.current = curr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueReturns, soundOn]);

  if (!noc) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center" data-testid="office-display-loading">
        <p className="text-zinc-500 font-bold flex items-center gap-3"><RefreshCw className="w-5 h-5 animate-spin" /> Cargando panel de oficina…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-hidden" data-testid="office-display">
      <style>{`@keyframes officeBlink { 0%,100% { border-color: rgba(255,204,0,0.15); box-shadow: none; } 50% { border-color: #FFCC00; box-shadow: 0 0 14px rgba(255,204,0,0.45); } } .office-blink { animation: officeBlink 1.6s ease-in-out infinite; }`}</style>
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
      <div className="relative max-w-[1800px] mx-auto px-8 py-6 min-h-screen flex flex-col">
        <header className="flex items-center justify-between gap-6 pb-6 border-b border-white/10">
          <div className="flex items-center gap-4">
            <img src="/logo-topacio.jpg" alt="Rent a Moto Topacio" className="h-16 w-auto rounded-xl border-2 border-brand shadow-[4px_4px_0_0_#FFCC00]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] font-extrabold text-brand">Panel en vivo</p>
              <h1 className="font-display font-black text-2xl leading-none mt-1">Rent a Moto Topacio</h1>
            </div>
          </div>
          <div className="flex items-center gap-5">
            {health && (() => {
              const top = health.issues?.[0];
              const extra = (health.issues?.length || 0) - 1;
              const label = health.status === "ok"
                ? "Servidor OK"
                : top ? `${top.title}${extra > 0 ? ` (+${extra})` : ""}` : `Servidor ${health.status}`;
              const tip = health.issues?.map((i) => `${i.title}: ${i.detail}`).join("  ·  ") || "Todo en orden";
              return (
                <span data-testid="office-health" title={tip} className={`badge-brutal ${healthTone} cursor-help`}>
                  <HardDrive className="w-3.5 h-3.5" /> {label}
                </span>
              );
            })()}
            {overdueReturns.length > 0 && (
              <span data-testid="office-overdue-count" className="badge-brutal bg-topacio-red text-white animate-pulse">
                <ArrowDown className="w-3.5 h-3.5" /> {overdueReturns.length} retrasada{overdueReturns.length > 1 ? "s" : ""}
              </span>
            )}
            {staleCount > 0 && (
              <span data-testid="office-review-count" className="badge-brutal bg-brand text-topacio-ink animate-pulse">
                ⚠ {staleCount} sin revisar
              </span>
            )}
            <div className="text-right">
              <div data-testid="office-clock" className="font-display font-black text-3xl leading-none">{now.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-zinc-500 mt-1">{now.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}</div>
            </div>
            <button data-testid="office-back-btn" onClick={() => navigate("/admin")}
              className="h-12 px-4 rounded-xl border-2 border-white/20 bg-white/5 text-white flex items-center gap-2 font-extrabold text-xs uppercase tracking-wider hover:bg-white/15 hover:-translate-y-0.5 transition-all">
              <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <button data-testid="office-sound-btn" onClick={toggleSound} title={soundOn ? "Silenciar avisos de retraso" : "Activar aviso sonoro de retraso"}
              className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center transition-transform hover:-translate-y-0.5 ${soundOn ? "border-topacio-green bg-topacio-green text-white" : "border-white/20 bg-white/5 text-zinc-300"}`}>
              {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button data-testid="office-fullscreen-btn" onClick={toggleFullscreen}
              className="w-12 h-12 rounded-xl border-2 border-brand bg-brand text-topacio-ink flex items-center justify-center hover:-translate-y-0.5 transition-transform">
              {fullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </header>

        <main className="flex-1 py-6 space-y-6">
          <section className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <KioskKpi label="Flota operativa" value={`${noc.fleet.operative}/${noc.fleet.total}`} sub={`${noc.fleet.workshop} no operativas`} icon={Bike} />
            <KioskKpi label="En la calle" value={onStreet} sub="alquileres activos ahora" icon={Activity} tone="text-topacio-green" />
            <KioskKpi label="Entregas hoy" value={noc.pickups_today.length} sub="recogidas previstas" icon={CalendarClock} tone="text-topacio-red" />
            <KioskKpi label="Devoluciones hoy" value={noc.returns_today.length} sub="retornos previstos" icon={ShieldCheck} tone="text-topacio-green" />
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 min-h-0">
            <div className="rounded-3xl border-2 border-white/10 bg-white/[0.035] p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-black text-xl">Estado de la flota</h2>
                <span className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-zinc-500">Actualización automática · 30 s</span>
              </div>
              <div className={`grid ${compactFleet ? "gap-2" : "gap-3"}`}
                style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${compactFleet ? 150 : 210}px, 1fr))` }}>
                {sortedBikes.map((bike) => {
                  const meta = statusMeta(bike.status);
                  const staleDays = (bike.status || "ok") === "ok" ? daysSinceReview(bike) : 0;
                  const stale = staleDays >= reviewThreshold;
                  return (
                    <div key={bike.slug} data-testid={`office-bike-${bike.slug}`} className={`rounded-xl border bg-black/30 ${compactFleet ? "p-2" : "p-3"} flex items-center gap-3 min-w-0 ${stale ? "office-blink border-brand/40" : "border-white/10"}`}>
                      <img src={bikeImg(bike)} alt="" onError={(e) => { e.currentTarget.src = "/logo-topacio.jpg"; }} className={`${compactFleet ? "w-11 h-8" : "w-16 h-12"} object-contain p-0.5 rounded-lg border border-white/10 bg-white shrink-0`} />
                      <div className="min-w-0 flex-1">
                        <FitText max={compactFleet ? 12 : 14} min={8} className="font-bold">{bike.name}</FitText>
                        <p className={`${compactFleet ? "text-[9px]" : "text-[10px]"} text-zinc-500 font-mono mt-0.5`}>Grupo {bike.group} · {bike.cc}cc</p>
                        <span className={`badge-brutal whitespace-nowrap ${compactFleet ? "!text-[11px] !px-2 !py-1" : "!text-xs !px-2.5 !py-1"} mt-1.5 ${meta.cls}`}>{statusLabel(bike.status)}</span>
                        {stale && (
                          <p data-testid={`office-review-alert-${bike.slug}`} className={`${compactFleet ? "text-[9px]" : "text-[10px]"} text-brand font-extrabold uppercase tracking-wider mt-1 animate-pulse`}>
                            {staleDays}d sin revisar
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <aside className="space-y-6">
              <div className="rounded-3xl border-2 border-white/10 bg-white/[0.035] p-5">
                <h2 className="font-display font-black text-xl mb-4">Hoy</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-topacio-green mb-2">Entregas</p>
                    {noc.pickups_today.length === 0 ? <p className="text-sm text-zinc-500 font-semibold">Sin entregas pendientes.</p> : noc.pickups_today.map((b) => (
                      <div key={b.ref} className="border-l-2 border-topacio-green pl-3 py-1.5">
                        <p className="font-bold text-sm">{b.name}</p><p className="text-xs text-zinc-500">{b.bike}{b.hotel ? ` · ${b.hotel}` : ""}</p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-topacio-red mb-2">Devoluciones</p>
                    {overdueReturns.length > 0 && (
                      <div data-testid="office-overdue-list" className="mb-3 rounded-xl border-2 border-topacio-red bg-topacio-red/10 p-2.5 animate-pulse">
                        <p className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-topacio-red mb-1.5 flex items-center gap-1.5">
                          <ArrowDown className="w-3.5 h-3.5" /> Retrasadas
                        </p>
                        {overdueReturns.map((b) => (
                          <div key={b.ref} data-testid={`office-overdue-${b.ref}`} className="flex items-center justify-between gap-2 py-1">
                            <div className="min-w-0">
                              <p className="font-bold text-sm truncate">{b.name}</p>
                              <p className="text-xs text-zinc-400 truncate">{b.bike} · {b.end_date ? `${b.end_date} ` : ""}{b.return_time}</p>
                            </div>
                            <span className="badge-brutal bg-topacio-red text-white !text-[9px] shrink-0 whitespace-nowrap">+{lateLabel(b.late_minutes)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {onTimeReturns.length === 0 && overdueReturns.length === 0 ? (
                      <p className="text-sm text-zinc-500 font-semibold">Sin devoluciones previstas.</p>
                    ) : onTimeReturns.map((b) => (
                      <div key={b.ref} className="border-l-2 border-topacio-red pl-3 py-1.5 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-sm truncate">{b.name}</p><p className="text-xs text-zinc-500 truncate">{b.bike}</p>
                        </div>
                        <span className="text-[10px] font-mono text-zinc-500 shrink-0">{b.return_time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="rounded-3xl border-2 border-brand bg-brand p-5 text-topacio-ink shadow-[6px_6px_0_0_#fff]">
                <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold">Ocupación 30 días</p>
                <p className="font-display font-black text-6xl leading-none mt-3">{noc.occupancy_30d}%</p>
              </div>
              {(noc.partner_ranking || []).length > 0 && (
                <div className="rounded-3xl border-2 border-white/10 bg-white/[0.035] p-5" data-testid="office-podium">
                  <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-brand" /> Podio de colaboradores
                  </h2>
                  <div className="space-y-3">
                    {noc.partner_ranking.slice(0, 3).map((p, i) => {
                      const medal = ["text-brand", "text-zinc-300", "text-amber-600"][i];
                      return (
                        <div key={p.code} data-testid={`office-podium-${p.code}`} className="flex items-center gap-3">
                          <Trophy className={`w-5 h-5 shrink-0 ${medal}`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{p.name}</p>
                            <p className="text-[10px] text-zinc-500 font-semibold">
                              {p.n} reservas · {p.revenue}€{p.goal ? ` · meta ${p.n}/${p.goal}` : ""}
                            </p>
                          </div>
                          {p.goal && (
                            <div className="w-16 h-2 rounded-full bg-white/10 overflow-hidden shrink-0">
                              <div className={`h-full ${p.n >= p.goal ? "bg-topacio-green" : "bg-brand"}`}
                                style={{ width: `${Math.min(100, Math.round((p.n / p.goal) * 100))}%` }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(noc.top_bikes_month || []).length > 0 && (
                    <div className="mt-5 pt-4 border-t border-white/10" data-testid="office-bike-ranking">
                      <h3 className="font-display font-black text-sm mb-3 flex items-center gap-2 text-zinc-300">
                        <Bike className="w-4 h-4 text-brand" /> Motos más alquiladas · mes
                      </h3>
                      <div className="space-y-2">
                        {noc.top_bikes_month.map((b, i) => (
                          <div key={b.name} data-testid={`office-bike-rank-${i}`} className="flex items-center gap-3">
                            <span className={`font-display font-black text-lg w-6 text-center ${["text-brand", "text-zinc-300", "text-amber-600"][i]}`}>{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm truncate">{b.name}</p>
                              <p className="text-[10px] text-zinc-500 font-semibold">{b.n} reservas · {b.revenue}€</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="rounded-3xl border-2 border-white/10 bg-white/[0.035] p-5" data-testid="office-revenue-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-zinc-500">Facturación del mes</p>
                    <p className="font-display font-black text-5xl leading-none mt-3">{eur(noc.revenue.month)}€</p>
                  </div>
                  {healthHistory?.uptime != null && (
                    <span className="badge-brutal bg-topacio-green text-white !text-[9px]">{healthHistory.uptime}% online 24h</span>
                  )}
                </div>
                <div className="flex items-end gap-1.5 h-16 mt-5">
                  {noc.revenue.monthly.slice(-6).map((m) => {
                    const max = Math.max(...noc.revenue.monthly.map((x) => x.v), 1);
                    return <div key={m.m} title={`${m.m}: ${eur(m.v)}€`} className="flex-1 rounded-t bg-brand/90 border border-brand" style={{ height: `${Math.max(8, (m.v / max) * 100)}%` }} />;
                  })}
                </div>
                <div className="flex justify-between text-[8px] font-mono text-zinc-500 mt-1.5">
                  {noc.revenue.monthly.slice(-6).map((m) => <span key={m.m}>{m.m}</span>)}
                </div>
                <p className="text-[10px] font-semibold text-zinc-500 mt-3">Web {eur(noc.channels?.online?.revenue)}€ · Partners {eur(noc.channels?.partner?.revenue)}€ · Tienda {eur(noc.channels?.tienda?.revenue)}€</p>
              </div>
            </aside>
          </section>

          <WeekOccupancy cal={cal} overdueRefs={overdueRefs} />
        </main>

        <footer className="pt-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-zinc-600">rentamototenerife.es · Costa Adeje</p>
          <SiempriaCredit dark className="!py-0" />
        </footer>
      </div>
    </div>
  );
}
